import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import CategoryNav from '@/components/feature/CategoryNav';
import { videos, categories } from '@/mocks/videos';
import { siteInfo, faqData } from '@/mocks/site';

const hotVideos = videos.filter(v => v.isHot).slice(0, 6);
const newVideos = videos.filter(v => v.isNew).slice(0, 6);
const allVideos = videos.slice(0, 8);

const structuredData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      name: '果冻传媒',
      url: 'https://www.guodongmedia.com/',
      description: siteInfo.description,
      email: siteInfo.email,
      telephone: siteInfo.phone,
      address: {
        '@type': 'PostalAddress',
        addressLocality: '北京',
        addressCountry: 'CN',
      },
    },
    {
      '@type': 'WebSite',
      name: '果冻传媒',
      url: 'https://www.guodongmedia.com/',
      description: siteInfo.description,
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://www.guodongmedia.com/search?q={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@type': 'WebPage',
      name: '果冻传媒在线观看·高清全集免费播放',
      description: siteInfo.description,
      url: 'https://www.guodongmedia.com/',
      isPartOf: { '@type': 'WebSite', name: '果冻传媒' },
    },
  ],
};

export default function Home() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Chinese%20cinematic%20film%20reel%20with%20warm%20golden%20and%20red%20abstract%20light%20trails%2C%20modern%20media%20entertainment%20concept%2C%20dramatic%20stage%20lighting%20effect%2C%20artistic%20bokeh%20background%2C%20luxurious%20dark%20texture%2C%20elegant%20cinema%20atmosphere&width=1800&height=900&seq=hero-home-2026&orientation=landscape"
            alt="果冻传媒 - 高清视频在线观看"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/55 via-foreground-950/35 to-foreground-950/70"></div>

          <div className="relative z-10 w-full max-w-[900px] mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight mb-4">
              果冻传媒在线观看
            </h1>
            <p className="text-base md:text-lg text-background-50/80 mb-2 max-w-[640px] mx-auto leading-relaxed">
              高清全集免费播放，汇聚全系列优质短剧与影视内容
            </p>
            <p className="text-sm text-background-50/60 mb-8 max-w-[520px] mx-auto">
              无需下载 · 流畅播放 · 多端观看 · 每日更新
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/category/all"
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium text-sm whitespace-nowrap cursor-pointer transition-all duration-200 transform hover:scale-105"
              >
                <i className="ri-play-circle-line mr-2"></i>
                立即观看
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-background-50/15 hover:bg-background-50/25 backdrop-blur-sm text-background-50 rounded-full font-medium text-sm whitespace-nowrap cursor-pointer transition-all duration-200"
              >
                了解更多
              </Link>
            </div>

            <div className="flex items-center justify-center gap-8 mt-10">
              {[
                { value: '5000万+', label: '累计用户' },
                { value: '2亿+', label: '日均播放' },
                { value: '1000+', label: '内容作品' },
                { value: '8大', label: '内容频道' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-xl md:text-2xl font-bold text-background-50">{stat.value}</div>
                  <div className="text-xs text-background-50/60 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-8 md:py-10">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <CategoryNav activeSlug="all" />
          </div>
        </section>

        {/* Hot Content */}
        <section className="py-6 md:py-10">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">大家都在看的热门内容</p>
              </div>
              <Link
                to="/category/all?sort=hot"
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {hotVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          </div>
        </section>

        {/* New Content */}
        <section className="py-6 md:py-10 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  最新上线
                </h2>
                <p className="text-sm text-foreground-500 mt-1">刚刚更新的新鲜内容</p>
              </div>
              <Link
                to="/category/all?sort=new"
                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {newVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          </div>
        </section>

        {/* All Content Grid */}
        <section className="py-10 md:py-14">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="mb-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                精选内容
              </h2>
              <p className="text-sm text-foreground-500 mt-1">品质好剧，值得一看</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {allVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
            <div className="flex justify-center mt-10">
              <Link
                to="/category/all"
                className="px-8 py-3 border-2 border-foreground-200 hover:border-primary-500 text-foreground-700 hover:text-primary-600 rounded-full font-medium text-sm whitespace-nowrap cursor-pointer transition-all duration-200"
              >
                浏览全部内容
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Feature Highlights */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                为什么选择果冻传媒
              </h2>
              <p className="text-sm text-foreground-500 mt-2">全方位优质观影体验</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { icon: 'ri-4k-line', title: '4K超高清', desc: '顶级画质体验' },
                { icon: 'ri-speed-up-line', title: '流畅播放', desc: '无需缓冲等待' },
                { icon: 'ri-smartphone-line', title: '多端同步', desc: '手机平板电脑' },
                { icon: 'ri-download-cloud-line', title: '无需下载', desc: '在线即点即看' },
              ].map((feat) => (
                <div key={feat.title} className="text-center p-6 rounded-2xl bg-background-50">
                  <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-100">
                    <i className={`${feat.icon} text-2xl text-primary-600`}></i>
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-900 mb-1">{feat.title}</h3>
                  <p className="text-sm text-foreground-500">{feat.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-10 md:py-14">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500 mt-2">快速了解果冻传媒</p>
            </div>
            <div className="flex flex-col gap-3">
              {faqData.map((faq, idx) => (
                <details key={idx} className="group bg-background-100 rounded-xl p-5 cursor-pointer">
                  <summary className="flex items-center justify-between font-medium text-foreground-900 text-sm md:text-base list-none">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-14 md:py-20 relative overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20background%20with%20soft%20flowing%20shapes%20in%20orange%20and%20gold%20tones%2C%20modern%20cinematic%20atmosphere%2C%20elegant%20minimalist%20texture%2C%20smooth%20curves&width=1800&height=600&seq=cta-home-2026&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-primary-600/90"></div>
          <div className="relative z-10 max-w-[700px] mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
              即刻开启观影之旅
            </h2>
            <p className="text-background-50/80 mb-8 text-sm md:text-base leading-relaxed">
              无需注册，无需下载，打开浏览器即可畅享海量高清视频内容
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center px-10 py-3.5 bg-background-50 hover:bg-background-100 text-primary-600 rounded-full font-bold text-sm whitespace-nowrap cursor-pointer transition-all duration-200 transform hover:scale-105"
            >
              <i className="ri-play-circle-fill text-lg mr-2"></i>
              开始观看
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}